import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
  FormControlLabel,
  FormControl,
  FormLabel,
  FormGroup,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  IconButton,
  Paper,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  AutoAwesome as AutoAwesomeIcon,
  Upload as UploadIcon,
  OpenInNew as OpenInNewIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import protocolRegistrationService from '../../services/protocolRegistration.service';
import api from '../../services/api';

// Feature flag: Set to true to show Auto-Populate button, false to hide it
const SHOW_AUTO_POPULATE_BUTTON = true; // Change to true to make button visible

const studyTypeOptions = ['Interventional', 'Observational', 'Expanded Access'];
const allocationOptions = ['Randomized', 'Non-Randomized'];
const genderOptions = ['Male', 'Female', 'All'];
const dateTypeOptions = ['Anticipated', 'Actual'];
const ethicsApprovalStatusOptions = ['Approved', 'Not approved', 'Pending', 'Not required'];

const primaryPurposeOptions = [
  'Treatment',
  'Prevention',
  'Diagnostic',
  'Supportive Care',
  'Screening',
  'Health Services Research',
  'Basic Science',
  'Other',
];
const phaseOptions = [
  'N/A',
  'Early Phase 1',
  'Phase 1',
  'Phase 1/Phase 2',
  'Phase 2',
  'Phase 2/Phase 3',
  'Phase 3',
  'Phase 4',
];
const interventionModelOptions = [
  'Single Group',
  'Parallel',
  'Cross-over',
  'Factorial',
  'Sequential',
];
const overallStatusOptions = [
  'Not yet recruiting',
  'Recruiting',
  'Enrolling by invitation',
  'Active, not recruiting',
  'Suspended',
  'Terminated',
  'Completed',
  'Withdrawn',
  'Unknown',
];
const maskingOptions = [
  'OPEN LABEL',
  'SINGLE BLIND',
  'DOUBLE BLIND',
];

const initialForm = {
  primary_registry_id: '',
  date_registration: '',
  secondary_ids: [{ agency: '', id: '' }],
  brief_title: '',
  official_title: '',
  acronym: '',
  protocol_id: '',
  primary_sponsor_name: '',
  primary_sponsor_type: '',
  primary_sponsor_address: {
    street: '',
    city: '',
    country: '',
    postal_code: '',
  },
  secondary_sponsors: [''],
  collaborators: [{ label: '', value: '' }],
  funders: [''],
  public_contact_name: '',
  public_contact_affiliation: '',
  public_contact_email: '',
  public_contact_phone: '',
  scientific_contact_name: '',
  scientific_contact_affiliation: '',
  scientific_contact_email: '',
  scientific_contact_phone: '',
  study_type: '',
  primary_purpose: '',
  phase: '',
  allocation: '',
  intervention_model: '',
  masking: '',
  observational_model: '',
  time_perspective: '',
  has_dmc: false,
  condition_name: [''],
  condition_code: [''],
  rare_disease: false,
  keywords: [''],
  inclusion_criteria: '',
  exclusion_criteria: '',
  gender: '',
  minimum_age: '',
  maximum_age: '',
  healthy_volunteers: false,
  overall_status: '',
  start_date: '',
  start_date_type: '',
  primary_completion_date: '',
  primary_completion_date_type: '',
  study_completion_date: '',
  study_completion_date_type: '',
  target_sample_size: '',
  enrollment_actual: '',
  recruitment_countries: [''],
  sites: [{ facility: '', city: '', state: '', country: '' }],
  primary_objective: '',
  secondary_objectives: '',
  primary_outcomes: [{ name: '', measure: '', timeframe: '', safety_issue: false }],
  secondary_outcomes: [{ name: '', measure: '', timeframe: '', safety_issue: false }],
  other_outcomes: [{ name: '', measure: '', timeframe: '', safety_issue: false }],
  interventions: [{ name: '', type: '', description: '' }],
  arms: [{ arm_title: '', arm_type: '', description: '', interventions: [''] }],
  ethics_approval_status: '',
  ethics_approval_date: '',
  ethics_committees: [''],
  committees: [''],
  is_fda_regulated_drug: false,
  is_fda_regulated_device: false,
  fda_ind_number: '',
  // India-specific canonical fields
  dcgi_approval_status: '',
  participant_type: '',
  post_graduation_thesis: false,
  ctri_trial_category: '',
  ctri_study_design: '',
  estimated_trial_duration: '',
  ipd_sharing: false,
  ipd_plan_description: '',
  ipd_plan_details: {
    what_data: '',
    when_available: '',
    who_can_access: '',
    access_criteria: '',
    data_location: '',
  },
  protocol_document: '',
  sap_document: '',
  csr_document: '',
  additional_documents: '',
};

// Local RegistrySelectorDialog component to avoid import/export issues
const RegistrySelectorDialog = ({ open, onClose, onConfirm, loading = false }) => {
  const [selectedRegistries, setSelectedRegistries] = useState({
    clinicaltrialsgov: false,
    who: false,
    eu: false,
    india: false,
    japan: false,
    anz: false,
  });

  const registryOptions = [
    {
      id: 'clinicaltrialsgov',
      label: 'ClinicalTrials.gov',
      description: 'US clinical trials registry (~60% auto-populatable)',
    },
    {
      id: 'who',
      label: 'WHO Registry',
      description: 'World Health Organization registry (~70% auto-populatable)',
    },
    {
      id: 'eu',
      label: 'European Union (CTIS)',
      description:
        'EU Clinical Trials Information System (~40% auto-populatable, many fields require manual entry)',
    },
    {
      id: 'india',
      label: 'India (CTRI)',
      description:
        'Clinical Trials Registry of India (CTRI). Core fields auto-populated; registry-specific details still require manual review.',
    },

    {
      id: 'japan',
      label: 'Japan Trial Registry (jRCT)',
      description:
        'Japan registry (jRCT). Core fields (titles, sponsor, design, outcomes, ethics) auto-populated from Core Datasheet; Japan-specific details still require manual review.',
    },
    {
      id: 'anz',
      label: 'Australia & New Zealand (ANZCTR)',
      description:
        'Australia & New Zealand Clinical Trials Registry. Core fields auto-populated from the Canonical Datasheet; ANZCTR-specific details still require manual review.',
    },
  ];

  const handleToggle = (registryId) => {
    setSelectedRegistries((prev) => ({
      ...prev,
      [registryId]: !prev[registryId],
    }));
  };

  const handleConfirm = () => {
    const selected = Object.keys(selectedRegistries).filter((key) => selectedRegistries[key]);

    if (selected.length === 0) {
      return; // Don't close if nothing selected
    }

    onConfirm(selected);
  };

  const handleClose = () => {
    // Reset selections when closing
    setSelectedRegistries({
      clinicaltrialsgov: false,
      who: false,
      eu: false,
      india: false,
      japan: false,
      anz:false,
    });
    onClose();
  };

  const selectedCount = Object.values(selectedRegistries).filter(Boolean).length;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoAwesomeIcon color="primary" />
          <Typography variant="h6">Auto-Populate Registries</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          Select one or more registries to auto-populate from the Core Datasheet. You can review and
          edit the auto-populated data before saving each registry form.
        </Alert>

        <FormControl component="fieldset" fullWidth>
          <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'medium' }}>
            Select Registries to Auto-Populate
          </FormLabel>
          <FormGroup>
            {registryOptions.map((option) => (
              <Box
                key={option.id}
                sx={{
                  p: 2,
                  mb: 1,
                  border: '1px solid',
                  borderColor: selectedRegistries[option.id] ? 'primary.main' : 'divider',
                  borderRadius: 1,
                  backgroundColor: selectedRegistries[option.id]
                    ? 'action.selected'
                    : 'background.paper',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedRegistries[option.id]}
                      onChange={() => handleToggle(option.id)}
                      disabled={loading}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight="medium">
                        {option.label}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {option.description}
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            ))}
          </FormGroup>
        </FormControl>

        {selectedCount > 0 && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {selectedCount} registry{selectedCount > 1 ? 'ies' : 'y'} selected
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          startIcon={<AutoAwesomeIcon />}
          disabled={selectedCount === 0 || loading}
        >
          {loading ? 'Auto-Populating...' : `Auto-Populate ${selectedCount > 0 ? `(${selectedCount})` : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const CanonicalDataset = ({ projectId }) => {
  const [formData, setFormData] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [autoPopulating, setAutoPopulating] = useState(false);
  const [registrySelectorOpen, setRegistrySelectorOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [uploadTarget, setUploadTarget] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loadingProtocolPdf, setLoadingProtocolPdf] = useState(false);
  /** Last auto-populate API payload (metadata includes USDM/CORE validation). Optional cache for download. */
  const [lastAutoPopulateReport, setLastAutoPopulateReport] = useState(null);
  const [validationReportDownloading, setValidationReportDownloading] = useState(false);
  const [sdrRegistering, setSdrRegistering] = useState(false);
  const [externalSdrPublishing, setExternalSdrPublishing] = useState(false);
  const [externalSdrDownloading, setExternalSdrDownloading] = useState(false);
  const [externalSdrUploadValidating, setExternalSdrUploadValidating] = useState(false);
  const [documentServicePushing, setDocumentServicePushing] = useState(false);
  const usdmExternalValidateInputRef = React.useRef(null);

  // Persist publish result in localStorage so download works across page refreshes.
  const sdrPublishKey = projectId ? `externalSdrPublish_${projectId}` : null;
  const loadSdrPublishCache = () => {
    if (!sdrPublishKey) return {};
    try { return JSON.parse(localStorage.getItem(sdrPublishKey) || '{}'); } catch { return {}; }
  };
  const saveSdrPublishCache = (data) => {
    if (!sdrPublishKey) return;
    try { localStorage.setItem(sdrPublishKey, JSON.stringify(data)); } catch {}
  };

  const [lastExternalSdrStudyDefinitionsLink, setLastExternalSdrStudyDefinitionsLinkRaw] = useState(() => loadSdrPublishCache().sdrLink || '');
  const [lastExternalSdrStudyId, setLastExternalSdrStudyIdRaw] = useState(() => loadSdrPublishCache().studyId || '');
  const [lastExternalSdrSdruploadversion, setLastExternalSdrSdruploadversionRaw] = useState(() => loadSdrPublishCache().sdruploadversion ?? null);

  const setLastExternalSdrStudyDefinitionsLink = (v) => { setLastExternalSdrStudyDefinitionsLinkRaw(v); };
  const setLastExternalSdrStudyId = (v) => { setLastExternalSdrStudyIdRaw(v); };
  const setLastExternalSdrSdruploadversion = (v) => { setLastExternalSdrSdruploadversionRaw(v); };
 
  const fileInputRef = React.useRef(null);
  const [expandedSections, setExpandedSections] = useState({
    registration: true,
    studyInfo: true,
    contacts: false,
    design: false,
    eligibility: false,
    outcomes: false,
    interventions: false,
    ethics: false,
    ipd: false,
    documents: false,
  });

  // Define showSnackbar early so it can be used in other functions
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

   useEffect(() => {
    setLastAutoPopulateReport(null);
    // Restore last publish result from localStorage for this project.
    const cached = loadSdrPublishCache();
    setLastExternalSdrStudyDefinitionsLink(cached.sdrLink || '');
    setLastExternalSdrStudyId(cached.studyId || '');
    setLastExternalSdrSdruploadversion(cached.sdruploadversion ?? null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const downloadValidationReport = async () => {
    if (!projectId) return;
    setValidationReportDownloading(true);
    try {
      let payload;
      const generatedAt = new Date().toISOString();
      const stamp = generatedAt.replace(/[:.]/g, '-').slice(0, 19);
      const safeProject = projectId || 'project';

      if (lastAutoPopulateReport?.metadata?.usdmValidation) {
        let usdmSnapshot = lastAutoPopulateReport.metadata?.usdm ?? null;
        if (!usdmSnapshot) {
          const res = await protocolRegistrationService.getUsdmValidationReport(projectId);
          if (res.success) usdmSnapshot = res.usdm ?? null;
        }
        payload = {
          reportType: 'canonical-dataset-auto-populate',
          projectId: lastAutoPopulateReport.projectId,
          generatedAt: lastAutoPopulateReport.generatedAt,
          message: lastAutoPopulateReport.message,
          metadata: lastAutoPopulateReport.metadata ?? null,
          usdm: usdmSnapshot,
          note: usdmSnapshot
            ? 'Snapshot from last Auto-Populate; USDM document attached (from session or fetched if missing)'
            : 'Snapshot from last Auto-Populate; USDM document unavailable',
        };
      } else {
        const res = await protocolRegistrationService.getUsdmValidationReport(projectId);
        if (!res.success) {
          showSnackbar(res.message || 'Failed to load validation report', 'error');
          return;
        }
        payload = {
          reportType: 'usdm-validation-report',
          projectId,
          generatedAt,
          message: res.message || '',
          metadata: res.metadata ?? null,
          usdm: res.usdm ?? null,
          note: 'Fetched from server (protocol XML → USDM → CORE/AJV)',
        };
      }

      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `usdm-core-validation-report-${safeProject}-${stamp}.json`;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showSnackbar('Validation report downloaded', 'success');
    } catch (err) {
      console.error('Validation report download failed:', err);
      showSnackbar(
        err.response?.data?.message || err.message || 'Failed to download validation report',
        'error'
      );
    } finally {
      setValidationReportDownloading(false);
    }
  };

  const registerToSdr = async () => {
    if (!projectId) return;
    setSdrRegistering(true);
    try {
      const res = await protocolRegistrationService.registerUsdmToSdr(projectId);
      if (res?.success) {
        const status = res?.status ? `status=${res.status}` : '';
        const instanceId = res?.usdmInstanceId ? `instance=${res.usdmInstanceId}` : '';
        showSnackbar(`USDM registered to SDR successfully ${status} ${instanceId}`.trim(), 'success');
      } else {
        showSnackbar(res?.message || 'Failed to register USDM to SDR', 'error');
      }
    } catch (err) {
      showSnackbar(err.response?.data?.message || err.message || 'Failed to register USDM to SDR', 'error');
    } finally {
      setSdrRegistering(false);
    }
  };

  const publishToExternalSdr = async () => {
    if (!projectId) return;
    setExternalSdrPublishing(true);
    setLastExternalSdrStudyDefinitionsLink('');
    setLastExternalSdrStudyId('');
    setLastExternalSdrSdruploadversion(null);
    try {
      // First attempt: run external CDISC conformance validation + publish.
      let res;
      try {
        res = await protocolRegistrationService.publishUsdmToExternalSdr(projectId);
      } catch (firstErr) {
        const body = firstErr.response?.data;
        const isCoreEngineCrash =
          firstErr.response?.status === 502 &&
          (body?.message?.includes('conformance') || body?.error?.message?.includes('CDISC Rules Engine'));

        if (isCoreEngineCrash) {
          // Auto-retry: CDISC Rules Engine crash inside SDR container (known bug).
          // Skip external conformance (local CORE already passed) and go straight to publish.
          console.warn('[Publish External SDR] CDISC engine crash detected — retrying without external conformance validation.');
          res = await protocolRegistrationService.publishUsdmToExternalSdr(projectId, { skipConformance: true });
        } else {
          throw firstErr;
        }
      }

      if (res?.success) {
        const studyId = res.externalPublish?.study?.id || res.externalPublish?.studyId || res.externalPublish?.id || '';
        const sdruploadversion = res.externalPublish?.auditTrail?.SDRUploadVersion ?? null;
        const studyDefinitionsLink = res.externalPublish?.links?.studyDefinitions || '';
        setLastExternalSdrStudyDefinitionsLink(studyDefinitionsLink);
        setLastExternalSdrStudyId(studyId);
        setLastExternalSdrSdruploadversion(sdruploadversion);
        // Persist so download works across page refreshes.
        saveSdrPublishCache({ sdrLink: studyDefinitionsLink, studyId, sdruploadversion });
        const warnMsg = res.conformanceWarning?.skipped
          ? ' (conformance validation skipped — CDISC engine bug in SDR container; update CDISC_RULES_ENGINE_RELATIVE_BINARY in ddf-sdr-platform .env)'
          : '';
        showSnackbar(
          `Published to external SDR successfully${studyId ? ` · studyId: ${studyId}` : ''}${warnMsg}`,
          res.conformanceWarning?.skipped ? 'warning' : 'success',
        );

        // Published to the external SDR does not guarantee the local mirror
        // (usdminstances) got updated — and that local copy is what the Data
        // Platform Document Service push reads on the next approval. A silent
        // gap here previously went unnoticed for days; surface it loudly.
        if (res.dbPersisted === false) {
          showSnackbar(
            `Warning: published to the external SDR, but the local copy failed to save (${res.persistWarning || 'unknown error'}). ` +
              'The Data Platform Document Service may push a stale/different USDM on the next approval until this is retried.',
            'error',
          );
        }
      } else {
        showSnackbar(res?.message || 'Failed to publish to external SDR', 'error');
      }
    } catch (err) {
      const data = err.response?.data;
      const msg =
        data?.message
        || data?.error
        || err.message
        || 'Failed to publish to external SDR';
      if (String(msg).includes('Route GET') && String(msg).includes('publish-external-sdr')) {
        showSnackbar(
          'Publish must use POST (click “Publish to External SDR” in the UI — do not open the API URL in the browser).',
          'error',
        );
      } else {
        showSnackbar(msg, 'error');
      }
    } finally {
      setExternalSdrPublishing(false);
    }
  };

  // Manually pushes protocol XML + USDM to the platform Data Platform
  // Document Service — same push that normally only fires automatically on
  // final approval (documentServicePush.service.js), triggerable on demand
  // instead of waiting for the next approval cycle.
  const sendToDocumentService = async () => {
    if (!projectId) return;
    setDocumentServicePushing(true);
    try {
      const res = await protocolRegistrationService.pushToDocumentService(projectId);
      const protocolStatus = res?.result?.protocol?.status;
      const usdmStatus = res?.result?.usdm?.status;
      // Which of the three resolveUsdmForPush branches produced the pushed USDM —
      // 'external-sdr-publish' (the SDR-normalized document), 'live-regeneration'
      // (mapped fresh from protocol XML), or 'last-published-usdm-instance' — shown
      // here so it's provable from the UI instead of inferred from the JSON shape.
      const usdmSource = res?.result?.usdm?.source;
      const usdmSourceLabel = {
        'external-sdr-publish': 'from external SDR publish',
        'live-regeneration': 'live-regenerated from protocol XML',
        'last-published-usdm-instance': 'from last published USDM instance',
      }[usdmSource];

      if (res?.success) {
        showSnackbar(
          `Sent to Data Platform · protocol: ${protocolStatus || 'unknown'} · USDM: ${usdmStatus || 'unknown'}` +
            (usdmSourceLabel ? ` (${usdmSourceLabel})` : ''),
          'success',
        );
      } else {
        showSnackbar(res?.message || 'Failed to send to Data Platform', 'error');
      }
    } catch (err) {
      const data = err.response?.data;
      const msg = data?.message || err.message || 'Failed to send to Data Platform';
      showSnackbar(msg, 'error');
    } finally {
      setDocumentServicePushing(false);
    }
  };

  const downloadPublishedUsdmFromExternalSdr = async () => {
    if (!projectId) return;
    setExternalSdrDownloading(true);
    try {
      const res = await protocolRegistrationService.downloadPublishedUsdmFromExternalSdr(projectId, {
        studyId: lastExternalSdrStudyId || undefined,
        sdruploadversion:
          lastExternalSdrSdruploadversion !== null && lastExternalSdrSdruploadversion !== undefined
            ? lastExternalSdrSdruploadversion
            : undefined,
        sdrLink: lastExternalSdrStudyDefinitionsLink || undefined,
      });
      if (!res?.success) {
        showSnackbar(res?.message || 'Failed to download from external SDR', 'error');
        return;
      }
      const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const sid = res.studyId || 'study';
      const blob = new Blob([JSON.stringify(res, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `external-sdr-published-usdm-${sid}-${stamp}.json`;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showSnackbar('Downloaded published USDM from external SDR', 'success');
    } catch (err) {
      showSnackbar(
        err.response?.data?.message || err.message || 'Failed to download from external SDR',
        'error'
      );
    } finally {
      setExternalSdrDownloading(false);
    }
  };

  const handleUsdmExternalValidateClick = () => {
    if (!projectId || externalSdrUploadValidating) return;
    usdmExternalValidateInputRef.current?.click();
  };

  const handleUsdmExternalValidateFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !projectId) return;

    setExternalSdrUploadValidating(true);
    try {
      const res = await protocolRegistrationService.validateUsdmJsonAgainstExternalSdr(
        projectId,
        file
      );
      const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const safeName = String(file.name || 'usdm').replace(/[^\w.-]+/g, '_');
      const blob = new Blob([JSON.stringify(res, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `usdm-external-sdr-conformance-${safeName}-${stamp}.json`;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      if (res?.success && res?.valid) {
        showSnackbar(
          res.message || 'USDM passed external SDR conformance validation. Report downloaded.',
          'success'
        );
      } else if (res?.success) {
        showSnackbar(
          res.message || 'External SDR validation finished with issues — report downloaded.',
          'warning'
        );
      } else {
        showSnackbar(res?.message || 'External SDR validation failed', 'error');
      }
    } catch (err) {
      const data = err.response?.data;
      if (data && typeof data === 'object') {
        try {
          const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
          const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `usdm-external-sdr-conformance-error-${stamp}.json`;
          a.rel = 'noopener';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        } catch {
          /* ignore download errors */
        }
      }
      showSnackbar(
        data?.message || err.message || 'Failed to validate USDM against external SDR',
        'error'
      );
    } finally {
      setExternalSdrUploadValidating(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleDocumentUploadClick = (fieldName) => {
    setUploadTarget(fieldName);
    fileInputRef.current?.click();
  };

  const handleOpenCurrentProtocolPdf = async () => {
    if (!projectId) return;
    setLoadingProtocolPdf(true);
    try {
      console.log('📄 Fetching Azure blob URL for project:', projectId);
      
      // Get the Azure blob URL with SAS token
      const response = await api.get(`/api/export/protocol-pdf-url/${projectId}`);
      
      if (!response.data || !response.data.success || !response.data.url) {
        throw new Error(response.data?.message || 'No PDF URL received from server');
      }
      
      const { url, filename, fileSize, exportDate } = response.data;
      console.log('✅ Received Azure blob URL:', {
        filename,
        fileSize,
        exportDate,
        url: url.substring(0, 100) + '...'
      });
      
      // Open the Azure blob URL directly in a new tab
      window.open(url, '_blank', 'noopener,noreferrer');
      
    } catch (err) {
      console.error('❌ Error opening protocol PDF:', err);
      
      let message = 'No protocol PDF available. Export protocol as PDF from Protocol Editor first.';
      
      if (err.response?.data?.message) {
        message = err.response.data.message;
      } else if (err.message) {
        message = err.message;
      }
      
      showSnackbar(message, 'error');
    } finally {
      setLoadingProtocolPdf(false);
    }
  };

  const handleDocumentFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadTarget) return;
    e.target.value = '';
    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      const response = await api.post('/upload', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const publicUrl = response.data?.publicUrl;
      if (!publicUrl) throw new Error('Upload failed');
      setFormData((prev) => ({ ...prev, [uploadTarget]: publicUrl }));
      showSnackbar('File uploaded. Link saved to field.', 'success');
    } catch (err) {
      showSnackbar(err.response?.data?.error || err.message || 'Upload failed', 'error');
    } finally {
      setUploading(false);
      setUploadTarget(null);
    }
  };

  // Load existing data on mount
  useEffect(() => {
    if (projectId) {
      loadExistingData();
    }
  }, [projectId]);

  const loadExistingData = async () => {
    if (!projectId) return;
    
    setLoading(true);
    try {
      const response = await protocolRegistrationService.getCanonicalDataset(projectId);
      if (response.success && response.data) {
        // Convert dates from backend format to input format
        const data = { ...response.data };
        if (data.date_registration) {
          data.date_registration = new Date(data.date_registration).toISOString().split('T')[0];
        }
        if (data.start_date) {
          data.start_date = new Date(data.start_date).toISOString().split('T')[0];
        }
        if (data.primary_completion_date) {
          data.primary_completion_date = new Date(data.primary_completion_date).toISOString().split('T')[0];
        }
        if (data.study_completion_date) {
          data.study_completion_date = new Date(data.study_completion_date).toISOString().split('T')[0];
        }
        if (data.ethics_approval_date) {
          data.ethics_approval_date = new Date(data.ethics_approval_date).toISOString().split('T')[0];
        }
        
        // Ensure arrays are initialized
        if (!data.secondary_ids || data.secondary_ids.length === 0) {
          data.secondary_ids = [{ agency: '', id: '' }];
        }
        if (!data.secondary_sponsors || data.secondary_sponsors.length === 0) {
          data.secondary_sponsors = [''];
        }
        if (!data.collaborators || data.collaborators.length === 0) {
          data.collaborators = [{ label: '', value: '' }];
        } else {
          data.collaborators = data.collaborators.map((c) =>
            typeof c === 'string' ? { label: '', value: c } : { label: c.label || '', value: c.value || '' }
          );
        }
        if (!data.funders || data.funders.length === 0) {
          data.funders = [''];
        }
        if (!data.condition_name || data.condition_name.length === 0) {
          data.condition_name = [''];
        }
        if (!data.condition_code || data.condition_code.length === 0) {
          data.condition_code = [''];
        }
        if (!data.keywords || data.keywords.length === 0) {
          data.keywords = [''];
        }
        if (!data.recruitment_countries || data.recruitment_countries.length === 0) {
          data.recruitment_countries = [''];
        }
        if (!data.sites || data.sites.length === 0) {
          data.sites = [{ facility: '', city: '', state: '', country: '' }];
        }
        if (!data.primary_outcomes || data.primary_outcomes.length === 0) {
          data.primary_outcomes = [{ name: '', measure: '', timeframe: '', safety_issue: false }];
        }
        if (!data.secondary_outcomes || data.secondary_outcomes.length === 0) {
          data.secondary_outcomes = [{ name: '', measure: '', timeframe: '', safety_issue: false }];
        }
        if (!data.other_outcomes || data.other_outcomes.length === 0) {
          data.other_outcomes = [{ name: '', measure: '', timeframe: '', safety_issue: false }];
        }
        if (!data.interventions || data.interventions.length === 0) {
          data.interventions = [{ name: '', type: '', description: '' }];
        }
        if (!data.arms || data.arms.length === 0) {
          data.arms = [{ arm_title: '', arm_type: '', description: '', interventions: [''] }];
        }
        if (!data.ethics_committees || data.ethics_committees.length === 0) {
          data.ethics_committees = [''];
        }
        if (!data.committees || data.committees.length === 0) {
          data.committees = [''];
        }
        if (!data.ipd_plan_details) {
          data.ipd_plan_details = {
            what_data: '',
            when_available: '',
            who_can_access: '',
            access_criteria: '',
            data_location: '',
          };
        }
        if (!data.primary_sponsor_address) {
          data.primary_sponsor_address = {
            street: '',
            city: '',
            country: '',
            postal_code: '',
          };
        }
        // Ensure India-specific canonical fields are initialized
        if (data.dcgi_approval_status === undefined) {
          data.dcgi_approval_status = '';
        }
        if (data.participant_type === undefined) {
          data.participant_type = '';
        }
        if (data.post_graduation_thesis === undefined) {
          data.post_graduation_thesis = false;
        }
        if (data.ctri_trial_category === undefined) {
          data.ctri_trial_category = '';
        }
        if (data.ctri_study_design === undefined) {
          data.ctri_study_design = '';
        }
        if (data.estimated_trial_duration === undefined) {
          data.estimated_trial_duration = '';
        }
        if (data.additional_documents === undefined) {
          data.additional_documents = '';
        }

        setFormData(data);
        showSnackbar('Data loaded successfully', 'success');
      }
    } catch (error) {
      console.error('Error loading canonical dataset:', error);
      showSnackbar('Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAutoPopulate = async () => {
    if (!projectId) {
      showSnackbar('Project ID is required', 'error');
      return;
    }

    setAutoPopulating(true);
    try {
      const response = await protocolRegistrationService.autoPopulateCanonicalDataset(projectId);
      console.log('[Frontend Auto-populate] Response received:', response);
      console.log('[Frontend Auto-populate] time_perspective from backend:', response.data?.time_perspective?.substring(0, 150));
      console.log('[Frontend Auto-populate] estimated_trial_duration from backend:', response.data?.estimated_trial_duration?.substring(0, 150));

      setLastAutoPopulateReport({
        projectId,
        generatedAt: new Date().toISOString(),
        message: response.message || '',
        metadata: response.metadata ?? null,
      });
      
      if (response.success && response.data) {
        // Merge auto-populated data with existing form data (don't overwrite existing values)
        const mergedData = { ...formData };
        console.log('[Frontend Auto-populate] Existing formData.time_perspective before merge:', formData.time_perspective?.substring(0, 150));
        
        Object.keys(response.data).forEach((key) => {
          if (response.data[key] !== null && response.data[key] !== undefined && response.data[key] !== '') {
            if (Array.isArray(response.data[key]) && response.data[key].length > 0) {
              mergedData[key] = response.data[key];
            } else if (!Array.isArray(response.data[key])) {
              mergedData[key] = response.data[key];
            }
          }
        });
        
        console.log('[Frontend Auto-populate] Merged time_perspective:', mergedData.time_perspective?.substring(0, 150));
        console.log('[Frontend Auto-populate] Merged estimated_trial_duration:', mergedData.estimated_trial_duration?.substring(0, 150));
        setFormData(mergedData);
        showSnackbar('Data auto-populated from Clinical Intake Form', 'success');
      } else {
        showSnackbar(response.message || 'No data available for auto-population', 'warning');
      }
    } catch (error) {
      console.error('Error auto-populating:', error);
      showSnackbar('Failed to auto-populate data', 'error');
    } finally {
      setAutoPopulating(false);
    }
  };

  const handleSave = async () => {
    if (!projectId) {
      showSnackbar('Project ID is required', 'error');
      return;
    }

    setSaving(true);
    try {
      const response = await protocolRegistrationService.saveCanonicalDataset(projectId, formData);
      if (response.success) {
        showSnackbar('Data saved successfully', 'success');
        
        // Optionally trigger auto-population of registries after save
        // User can also manually trigger this from registry forms
        // Uncomment the following lines if you want automatic auto-population on save:
        /*
        try {
          await protocolRegistrationService.autoPopulateRegistries(projectId);
          showSnackbar('Registry forms auto-populated. Review and edit as needed.', 'info');
        } catch (autoPopError) {
          console.warn('Auto-population failed (non-critical):', autoPopError);
          // Don't show error to user - auto-population is optional
        }
        */
      } else {
        showSnackbar(response.message || 'Failed to save data', 'error');
      }
    } catch (error) {
      console.error('Error saving canonical dataset:', error);
      showSnackbar(error.response?.data?.message || 'Failed to save data', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenRegistrySelector = () => {
    setRegistrySelectorOpen(true);
  };

  const handleCloseRegistrySelector = () => {
    setRegistrySelectorOpen(false);
  };

  const handleAutoPopulateRegistries = async (selectedRegistries) => {
    if (!projectId) {
      showSnackbar('Project ID is required', 'error');
      return;
    }

    if (!selectedRegistries || selectedRegistries.length === 0) {
      showSnackbar('Please select at least one registry', 'warning');
      return;
    }

    setAutoPopulating(true);
    setRegistrySelectorOpen(false);

    try {
      // Prefer the multi-registry endpoint if available
      let response;
      if (typeof protocolRegistrationService.autoPopulateRegistries === 'function') {
        response = await protocolRegistrationService.autoPopulateRegistries(
          projectId,
          selectedRegistries
        );
      } else {
        // Note: api instance already has /api in baseURL, so we don't need it here
        const apiResponse = await api.post(
          `/protocol-registration/auto-populate-registries/${projectId}`,
          { selectedRegistries }
        );
        response = apiResponse.data;
      }

      if (response.success) {
        const registryNames = selectedRegistries
          .map(r => {
            if (r === 'clinicaltrialsgov') return 'ClinicalTrials.gov';
            if (r === 'who') return 'WHO Registry';
            if (r === 'eu') return 'EU (CTIS) Registry';
            if (r === 'india') return 'India (CTRI) Registry';
            if (r === 'japan') return 'Japan (jRCT) Registry';
            return r;
          })
          .join(', ');

        showSnackbar(
          `${registryNames} auto-populated successfully! Navigate to registry tabs to review, edit, and save.`,
          'success'
        );
      } else {
        showSnackbar(response.message || 'Failed to auto-populate registries', 'error');
      }
    } catch (error) {
      // If the multi-registry route is missing (404), fall back to per-registry endpoint
      if (error.response?.status === 404) {
        try {
          await Promise.all(
            selectedRegistries.map((registryType) =>
              api.post(
                `/protocol-registration/auto-populate-registry/${projectId}/${registryType}`
              )
            )
          );

          const registryNames = selectedRegistries
            .map(r => {
              if (r === 'clinicaltrialsgov') return 'ClinicalTrials.gov';
              if (r === 'who') return 'WHO Registry';
              if (r === 'eu') return 'EU (CTIS) Registry';
              if (r === 'india') return 'India (CTRI) Registry';
              if (r === 'japan') return 'Japan Trial Registry (jRCT)';
              return r;
            })
            .join(', ');

          showSnackbar(
            `${registryNames} auto-populated successfully! Navigate to registry tabs to review, edit, and save.`,
            'success'
          );
        } catch (fallbackError) {
          console.error('Fallback auto-populate failed:', fallbackError);
          showSnackbar(
            fallbackError.response?.data?.message || 'Failed to auto-populate registries',
            'error'
          );
        }
      } else {
        console.error('Error auto-populating registries:', error);
        showSnackbar(
          error.response?.data?.message || 'Failed to auto-populate registries',
          'error'
        );
      }
    } finally {
      setAutoPopulating(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const keys = field.split('.');
      if (keys.length === 1) {
        return { ...prev, [field]: value };
      }
      const newData = { ...prev };
      let current = newData;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  const handleArrayChange = (field, index, value) => {
    setFormData((prev) => {
      const newArray = [...prev[field]];
      newArray[index] = value;
      return { ...prev, [field]: newArray };
    });
  };

  const handleAddArrayItem = (field, defaultItem) => {
    setFormData((prev) => ({
      ...prev,
      [field]: [...prev[field], defaultItem],
    }));
  };

  const handleRemoveArrayItem = (field, index) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
    }));
  };

  const handleNestedArrayChange = (field, index, subField, value) => {
    setFormData((prev) => {
      const newArray = [...prev[field]];
      newArray[index] = { ...newArray[index], [subField]: value };
      return { ...prev, [field]: newArray };
    });
  };

  const handleSectionToggle = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6" gutterBottom>
            Clinical Trial Registration Canonical Dataset
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Complete the canonical data schema for core clinical trial registration and results information.
          </Typography>
        </Box>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          {SHOW_AUTO_POPULATE_BUTTON && (
            <Button
              variant="outlined"
              startIcon={autoPopulating ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
              onClick={handleAutoPopulate}
              disabled={autoPopulating || !projectId}
            >
              Auto-Populate
            </Button>
          )}
          {SHOW_AUTO_POPULATE_BUTTON && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={
                validationReportDownloading ? <CircularProgress size={16} /> : <DownloadIcon />
              }
              onClick={downloadValidationReport}
              disabled={!projectId || validationReportDownloading}
              title="Download USDM / CORE (AJV) validation as JSON. Uses last Auto-Populate snapshot if available; otherwise fetches a fresh report from the server."
            >
              {validationReportDownloading ? 'Preparing…' : 'Download validation report'}
            </Button>
          )}
          <Button
            variant="outlined"
            color="secondary"
            startIcon={sdrRegistering ? <CircularProgress size={16} /> : <UploadIcon />}
            onClick={registerToSdr}
            disabled={!projectId || sdrRegistering}
            title="Map protocol XML to USDM, run CORE validation, then persist into SDR (USDMInstance)."
          >
            {sdrRegistering ? 'Registering…' : 'Register to SDR'}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={externalSdrPublishing ? <CircularProgress size={16} /> : <UploadIcon />}
            onClick={publishToExternalSdr}
            disabled={!projectId || externalSdrPublishing}
            title="Validate USDM conformance and publish to an external TransCelerate SDR API (configured by EXTERNAL_SDR_* env vars on the server)."
          >
            {externalSdrPublishing ? 'Publishing…' : 'Publish to External SDR'}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={documentServicePushing ? <CircularProgress size={16} /> : <UploadIcon />}
            onClick={sendToDocumentService}
            disabled={!projectId || documentServicePushing}
            title="Send the current protocol XML and USDM to the platform Data Platform Document Service now, instead of waiting for the next approval."
          >
            {documentServicePushing ? 'Sending…' : 'Send to Data Platform'}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={externalSdrUploadValidating ? <CircularProgress size={16} /> : <UploadIcon />}
            onClick={handleUsdmExternalValidateClick}
            disabled={!projectId || externalSdrUploadValidating || externalSdrPublishing}
            title="Upload a USDM JSON file and validate it against the external TransCelerate SDR conformance API only (does not publish; no local CORE)."
          >
            {externalSdrUploadValidating ? 'Validating…' : 'Upload USDM → Validate External SDR'}
          </Button>
          <input
            ref={usdmExternalValidateInputRef}
            type="file"
            accept="application/json,.json"
            style={{ display: 'none' }}
            onChange={handleUsdmExternalValidateFile}
          />
          <Button
            variant="outlined"
            color="secondary"
            startIcon={externalSdrDownloading ? <CircularProgress size={16} /> : <DownloadIcon />}
            onClick={downloadPublishedUsdmFromExternalSdr}
            disabled={!projectId || externalSdrDownloading || externalSdrPublishing}
            title="Download the study definition JSON from the external SDR (GET /v5/studydefinitions/{studyId}). Uses study id from mapped USDM; optional query params can be added on the server later."
          >
            {externalSdrDownloading ? 'Downloading…' : 'Download USDM (external SDR)'}
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving || !projectId}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={autoPopulating ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
            onClick={handleOpenRegistrySelector}
            disabled={autoPopulating || !projectId}
            title="Select registries to auto-populate from Core Datasheet"
          >
            {autoPopulating ? 'Auto-Populating...' : 'Auto-Populate Registries'}
          </Button>
        </Stack>
      </Box>

      {/* Registration Information */}
      <Accordion expanded={expandedSections.registration} onChange={() => handleSectionToggle('registration')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight="medium">
            1. Registration Information
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Primary Registry ID"
                value={formData.primary_registry_id}
                onChange={(e) => handleChange('primary_registry_id', e.target.value)}
                placeholder="e.g., CTRI:CTRI/2025/01/012345"
                helperText="Primary registry name and trial ID"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Date of Registration"
                value={formData.date_registration}
                onChange={(e) => handleChange('date_registration', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Secondary IDs
              </Typography>
              {formData.secondary_ids.map((item, index) => (
                <Grid container spacing={2} key={index} sx={{ mb: 1 }}>
                  <Grid item xs={12} md={5}>
                    <TextField
                      fullWidth
                      label="Agency"
                      value={item.agency}
                      onChange={(e) => handleNestedArrayChange('secondary_ids', index, 'agency', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <TextField
                      fullWidth
                      label="ID"
                      value={item.id}
                      onChange={(e) => handleNestedArrayChange('secondary_ids', index, 'id', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <IconButton
                      onClick={() => handleRemoveArrayItem('secondary_ids', index)}
                      disabled={formData.secondary_ids.length === 1}
                    >
                      <DeleteIcon />
                    </IconButton>
                    {index === formData.secondary_ids.length - 1 && (
                      <IconButton onClick={() => handleAddArrayItem('secondary_ids', { agency: '', id: '' })}>
                        <AddIcon />
                      </IconButton>
                    )}
                  </Grid>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Study Information */}
      <Accordion expanded={expandedSections.studyInfo} onChange={() => handleSectionToggle('studyInfo')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight="medium">
            2. Study Information
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Brief Title"
                value={formData.brief_title}
                onChange={(e) => handleChange('brief_title', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Official Title"
                value={formData.official_title}
                onChange={(e) => handleChange('official_title', e.target.value)}
                multiline
                rows={2}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Acronym"
                value={formData.acronym}
                onChange={(e) => handleChange('acronym', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Protocol ID"
                value={formData.protocol_id}
                onChange={(e) => handleChange('protocol_id', e.target.value)}
                helperText="Sponsor's internal protocol number"
              />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Primary Sponsor
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Primary Sponsor Name"
                value={formData.primary_sponsor_name}
                onChange={(e) => handleChange('primary_sponsor_name', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Primary Sponsor Type"
                value={formData.primary_sponsor_type}
                onChange={(e) => handleChange('primary_sponsor_type', e.target.value)}
                placeholder="e.g., Government, Industry, Academic"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Street"
                value={formData.primary_sponsor_address.street}
                onChange={(e) => handleChange('primary_sponsor_address.street', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="City"
                value={formData.primary_sponsor_address.city}
                onChange={(e) => handleChange('primary_sponsor_address.city', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Country"
                value={formData.primary_sponsor_address.country}
                onChange={(e) => handleChange('primary_sponsor_address.country', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Postal Code"
                value={formData.primary_sponsor_address.postal_code}
                onChange={(e) => handleChange('primary_sponsor_address.postal_code', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Secondary Sponsors
              </Typography>
              {formData.secondary_sponsors.map((item, index) => (
                <Grid container spacing={2} key={index} sx={{ mb: 1 }}>
                  <Grid item xs={10}>
                    <TextField
                      fullWidth
                      label={`Secondary Sponsor ${index + 1}`}
                      value={item}
                      onChange={(e) => handleArrayChange('secondary_sponsors', index, e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <IconButton
                      onClick={() => handleRemoveArrayItem('secondary_sponsors', index)}
                      disabled={formData.secondary_sponsors.length === 1}
                    >
                      <DeleteIcon />
                    </IconButton>
                    {index === formData.secondary_sponsors.length - 1 && (
                      <IconButton onClick={() => handleAddArrayItem('secondary_sponsors', '')}>
                        <AddIcon />
                      </IconButton>
                    )}
                  </Grid>
                </Grid>
              ))}
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Funders
              </Typography>
              {formData.funders.map((item, index) => (
                <Grid container spacing={2} key={index} sx={{ mb: 1 }}>
                  <Grid item xs={10}>
                    <TextField
                      fullWidth
                      label={`Funder ${index + 1}`}
                      value={item}
                      onChange={(e) => handleArrayChange('funders', index, e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <IconButton
                      onClick={() => handleRemoveArrayItem('funders', index)}
                      disabled={formData.funders.length === 1}
                    >
                      <DeleteIcon />
                    </IconButton>
                    {index === formData.funders.length - 1 && (
                      <IconButton onClick={() => handleAddArrayItem('funders', '')}>
                        <AddIcon />
                      </IconButton>
                    )}
                  </Grid>
                </Grid>
              ))}
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Collaborators (editable name and value; can add more)
              </Typography>
              {formData.collaborators.map((item, index) => (
                <Paper key={index} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={5}>
                      <TextField
                        fullWidth
                        label="Name / Label"
                        value={item.label || ''}
                        onChange={(e) => handleNestedArrayChange('collaborators', index, 'label', e.target.value)}
                        placeholder="e.g. Collaborator 1, Partner"
                      />
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <TextField
                        fullWidth
                        label="Value"
                        value={item.value || ''}
                        onChange={(e) => handleNestedArrayChange('collaborators', index, 'value', e.target.value)}
                        placeholder="Collaborator name or description"
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <IconButton
                        onClick={() => handleRemoveArrayItem('collaborators', index)}
                        disabled={formData.collaborators.length === 1}
                      >
                        <DeleteIcon />
                      </IconButton>
                      {index === formData.collaborators.length - 1 && (
                        <IconButton
                          onClick={() => handleAddArrayItem('collaborators', { label: '', value: '' })}
                        >
                          <AddIcon />
                        </IconButton>
                      )}
                    </Grid>
                  </Grid>
                </Paper>
              ))}
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Contacts */}
      <Accordion expanded={expandedSections.contacts} onChange={() => handleSectionToggle('contacts')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight="medium">
            3. Contact Information
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Public Contact
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Public Contact Name"
                value={formData.public_contact_name}
                onChange={(e) => handleChange('public_contact_name', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Public Contact Affiliation"
                value={formData.public_contact_affiliation}
                onChange={(e) => handleChange('public_contact_affiliation', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="email"
                label="Public Contact Email"
                value={formData.public_contact_email}
                onChange={(e) => handleChange('public_contact_email', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Public Contact Phone"
                value={formData.public_contact_phone}
                onChange={(e) => handleChange('public_contact_phone', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Scientific Contact
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Scientific Contact Name"
                value={formData.scientific_contact_name}
                onChange={(e) => handleChange('scientific_contact_name', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Scientific Contact Affiliation"
                value={formData.scientific_contact_affiliation}
                onChange={(e) => handleChange('scientific_contact_affiliation', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="email"
                label="Scientific Contact Email"
                value={formData.scientific_contact_email}
                onChange={(e) => handleChange('scientific_contact_email', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Scientific Contact Phone"
                value={formData.scientific_contact_phone}
                onChange={(e) => handleChange('scientific_contact_phone', e.target.value)}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Study Design */}
      <Accordion expanded={expandedSections.design} onChange={() => handleSectionToggle('design')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight="medium">
            4. Study Design
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Study Type"
                value={formData.study_type}
                onChange={(e) => handleChange('study_type', e.target.value)}
                required
              >
                {studyTypeOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Primary Purpose"
                value={formData.primary_purpose || ''}
                onChange={(e) => handleChange('primary_purpose', e.target.value)}
              >
                <MenuItem value="">
                  <em>Select</em>
                </MenuItem>
                {primaryPurposeOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Phase"
                value={formData.phase || ''}
                onChange={(e) => handleChange('phase', e.target.value)}
              >
                <MenuItem value="">
                  <em>Select</em>
                </MenuItem>
                {phaseOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Allocation"
                value={formData.allocation}
                onChange={(e) => handleChange('allocation', e.target.value)}
              >
                {allocationOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Intervention Model"
                value={formData.intervention_model || ''}
                onChange={(e) => handleChange('intervention_model', e.target.value)}
              >
                <MenuItem value="">
                  <em>Select</em>
                </MenuItem>
                {interventionModelOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Masking"
                value={formData.masking || ''}
                onChange={(e) => handleChange('masking', e.target.value)}
              >
                <MenuItem value="">
                  <em>Select</em>
                </MenuItem>
                {maskingOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Observational Model"
                value={formData.observational_model}
                onChange={(e) => handleChange('observational_model', e.target.value)}
                placeholder="e.g., Cohort, Case-Control"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Time Perspective (Duration from synopsis section 1)"
                value={formData.time_perspective}
                onChange={(e) => handleChange('time_perspective', e.target.value)}
                placeholder="e.g., from synopsis 1.1.2 Duration; or Prospective, Retrospective"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.post_graduation_thesis}
                    onChange={(e) => handleChange('post_graduation_thesis', e.target.checked)}
                  />
                }
                label="Post-graduate thesis trial (CTRI)"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="CTRI Trial Category"
                value={formData.ctri_trial_category}
                onChange={(e) => handleChange('ctri_trial_category', e.target.value)}
                placeholder="e.g., Drug, Device, Surgical, Behavioral"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="CTRI Study Design (free text)"
                value={formData.ctri_study_design}
                onChange={(e) => handleChange('ctri_study_design', e.target.value)}
                placeholder="Optional narrative design description used for India registry"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.has_dmc}
                    onChange={(e) => handleChange('has_dmc', e.target.checked)}
                  />
                }
                label="Has Data Monitoring Committee (DMC)"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Committees (add more committees)
              </Typography>
              {formData.committees.map((item, index) => (
                <Grid container spacing={2} key={index} sx={{ mb: 1 }}>
                  <Grid item xs={10}>
                    <TextField
                      fullWidth
                      label={`Committee ${index + 1}`}
                      value={item}
                      onChange={(e) => handleArrayChange('committees', index, e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <IconButton
                      onClick={() => handleRemoveArrayItem('committees', index)}
                      disabled={formData.committees.length === 1}
                    >
                      <DeleteIcon />
                    </IconButton>
                    {index === formData.committees.length - 1 && (
                      <IconButton onClick={() => handleAddArrayItem('committees', '')}>
                        <AddIcon />
                      </IconButton>
                    )}
                  </Grid>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Eligibility */}
      <Accordion expanded={expandedSections.eligibility} onChange={() => handleSectionToggle('eligibility')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight="medium">
            5. Eligibility Criteria
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Conditions
              </Typography>
            </Grid>
            <Grid item xs={12}>
              {formData.condition_name.map((item, index) => (
                <Grid container spacing={2} key={index} sx={{ mb: 1 }}>
                  <Grid item xs={10}>
                    <TextField
                      fullWidth
                      label={`Condition Name ${index + 1}`}
                      value={item}
                      onChange={(e) => handleArrayChange('condition_name', index, e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <IconButton
                      onClick={() => handleRemoveArrayItem('condition_name', index)}
                      disabled={formData.condition_name.length === 1}
                    >
                      <DeleteIcon />
                    </IconButton>
                    {index === formData.condition_name.length - 1 && (
                      <IconButton onClick={() => handleAddArrayItem('condition_name', '')}>
                        <AddIcon />
                      </IconButton>
                    )}
                  </Grid>
                </Grid>
              ))}
            </Grid>
            <Grid item xs={12}>
              {formData.condition_code.map((item, index) => (
                <Grid container spacing={2} key={index} sx={{ mb: 1 }}>
                  <Grid item xs={10}>
                    <TextField
                      fullWidth
                      label={`Condition Code ${index + 1} (ICD-10/MeSH)`}
                      value={item}
                      onChange={(e) => handleArrayChange('condition_code', index, e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <IconButton
                      onClick={() => handleRemoveArrayItem('condition_code', index)}
                      disabled={formData.condition_code.length === 1}
                    >
                      <DeleteIcon />
                    </IconButton>
                    {index === formData.condition_code.length - 1 && (
                      <IconButton onClick={() => handleAddArrayItem('condition_code', '')}>
                        <AddIcon />
                      </IconButton>
                    )}
                  </Grid>
                </Grid>
              ))}
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.rare_disease}
                    onChange={(e) => handleChange('rare_disease', e.target.checked)}
                  />
                }
                label="Rare Disease"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Keywords
              </Typography>
              {formData.keywords.map((item, index) => (
                <Grid container spacing={2} key={index} sx={{ mb: 1 }}>
                  <Grid item xs={10}>
                    <TextField
                      fullWidth
                      label={`Keyword ${index + 1}`}
                      value={item}
                      onChange={(e) => handleArrayChange('keywords', index, e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <IconButton
                      onClick={() => handleRemoveArrayItem('keywords', index)}
                      disabled={formData.keywords.length === 1}
                    >
                      <DeleteIcon />
                    </IconButton>
                    {index === formData.keywords.length - 1 && (
                      <IconButton onClick={() => handleAddArrayItem('keywords', '')}>
                        <AddIcon />
                      </IconButton>
                    )}
                  </Grid>
                </Grid>
              ))}
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Inclusion Criteria"
                value={formData.inclusion_criteria}
                onChange={(e) => handleChange('inclusion_criteria', e.target.value)}
                multiline
                rows={4}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Exclusion Criteria"
                value={formData.exclusion_criteria}
                onChange={(e) => handleChange('exclusion_criteria', e.target.value)}
                multiline
                rows={4}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Participant Type (CTRI)"
                value={formData.participant_type}
                onChange={(e) => handleChange('participant_type', e.target.value)}
                placeholder="e.g., Healthy volunteers, Patients, Others"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                label="Gender"
                value={formData.gender || ''}
                onChange={(e) => handleChange('gender', e.target.value)}
              >
                <MenuItem value="">
                  <em>Select</em>
                </MenuItem>
                {genderOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Minimum Age"
                value={formData.minimum_age}
                onChange={(e) => handleChange('minimum_age', e.target.value)}
                placeholder="e.g., 18 Years"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Maximum Age"
                value={formData.maximum_age}
                onChange={(e) => handleChange('maximum_age', e.target.value)}
                placeholder="e.g., 65 Years"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.healthy_volunteers}
                    onChange={(e) => handleChange('healthy_volunteers', e.target.checked)}
                  />
                }
                label="Accepts Healthy Volunteers"
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Study Status & Dates */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight="medium">
            6. Study Status & Dates
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Overall Status"
                value={formData.overall_status || ''}
                onChange={(e) => handleChange('overall_status', e.target.value)}
                required
              >
                <MenuItem value="">
                  <em>Select</em>
                </MenuItem>
                {overallStatusOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                value={formData.start_date}
                onChange={(e) => handleChange('start_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                select
                label="Start Date Type"
                value={formData.start_date_type}
                onChange={(e) => handleChange('start_date_type', e.target.value)}
              >
                {dateTypeOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="date"
                label="Primary Completion Date"
                value={formData.primary_completion_date}
                onChange={(e) => handleChange('primary_completion_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                select
                label="Primary Completion Date Type"
                value={formData.primary_completion_date_type}
                onChange={(e) => handleChange('primary_completion_date_type', e.target.value)}
              >
                {dateTypeOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="date"
                label="Study Completion Date"
                value={formData.study_completion_date}
                onChange={(e) => handleChange('study_completion_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                select
                label="Study Completion Date Type"
                value={formData.study_completion_date_type}
                onChange={(e) => handleChange('study_completion_date_type', e.target.value)}
              >
                {dateTypeOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Target Sample Size"
                value={formData.target_sample_size}
                onChange={(e) => handleChange('target_sample_size', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Actual Enrollment"
                value={formData.enrollment_actual}
                onChange={(e) => handleChange('enrollment_actual', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Estimated Trial Duration (CTRI)"
                value={formData.estimated_trial_duration}
                onChange={(e) => handleChange('estimated_trial_duration', e.target.value)}
                placeholder="e.g., 24 months"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Recruitment Countries
              </Typography>
              {formData.recruitment_countries.map((item, index) => (
                <Grid container spacing={2} key={index} sx={{ mb: 1 }}>
                  <Grid item xs={10}>
                    <TextField
                      fullWidth
                      label={`Country ${index + 1}`}
                      value={item}
                      onChange={(e) => handleArrayChange('recruitment_countries', index, e.target.value)}
                      required
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <IconButton
                      onClick={() => handleRemoveArrayItem('recruitment_countries', index)}
                      disabled={formData.recruitment_countries.length === 1}
                    >
                      <DeleteIcon />
                    </IconButton>
                    {index === formData.recruitment_countries.length - 1 && (
                      <IconButton onClick={() => handleAddArrayItem('recruitment_countries', '')}>
                        <AddIcon />
                      </IconButton>
                    )}
                  </Grid>
                </Grid>
              ))}
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Sites
              </Typography>
              {formData.sites.map((site, index) => (
                <Paper key={index} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Facility"
                        value={site.facility}
                        onChange={(e) => handleNestedArrayChange('sites', index, 'facility', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="City"
                        value={site.city}
                        onChange={(e) => handleNestedArrayChange('sites', index, 'city', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="State"
                        value={site.state}
                        onChange={(e) => handleNestedArrayChange('sites', index, 'state', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Country"
                        value={site.country}
                        onChange={(e) => handleNestedArrayChange('sites', index, 'country', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <IconButton
                        onClick={() => handleRemoveArrayItem('sites', index)}
                        disabled={formData.sites.length === 1}
                      >
                        <DeleteIcon />
                      </IconButton>
                      {index === formData.sites.length - 1 && (
                        <IconButton
                          onClick={() => handleAddArrayItem('sites', { facility: '', city: '', state: '', country: '' })}
                        >
                          <AddIcon />
                        </IconButton>
                      )}
                    </Grid>
                  </Grid>
                </Paper>
              ))}
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Objectives & Outcomes */}
      <Accordion expanded={expandedSections.outcomes} onChange={() => handleSectionToggle('outcomes')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight="medium">
            7. Objectives & Outcomes
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Primary Objective"
                value={formData.primary_objective}
                onChange={(e) => handleChange('primary_objective', e.target.value)}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Secondary Objectives"
                value={formData.secondary_objectives}
                onChange={(e) => handleChange('secondary_objectives', e.target.value)}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Primary Outcomes
              </Typography>
              {formData.primary_outcomes.map((outcome, index) => (
                <Paper key={index} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Name"
                        value={outcome.name}
                        onChange={(e) => handleNestedArrayChange('primary_outcomes', index, 'name', e.target.value)}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Measure"
                        value={outcome.measure}
                        onChange={(e) => handleNestedArrayChange('primary_outcomes', index, 'measure', e.target.value)}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Timeframe"
                        value={outcome.timeframe}
                        onChange={(e) => handleNestedArrayChange('primary_outcomes', index, 'timeframe', e.target.value)}
                        required
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={outcome.safety_issue}
                            onChange={(e) => handleNestedArrayChange('primary_outcomes', index, 'safety_issue', e.target.checked)}
                          />
                        }
                        label="Safety Issue"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <IconButton
                        onClick={() => handleRemoveArrayItem('primary_outcomes', index)}
                        disabled={formData.primary_outcomes.length === 1}
                      >
                        <DeleteIcon />
                      </IconButton>
                      {index === formData.primary_outcomes.length - 1 && (
                        <IconButton
                          onClick={() => handleAddArrayItem('primary_outcomes', { name: '', measure: '', timeframe: '', safety_issue: false })}
                        >
                          <AddIcon />
                        </IconButton>
                      )}
                    </Grid>
                  </Grid>
                </Paper>
              ))}
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Secondary Outcomes
              </Typography>
              {formData.secondary_outcomes.map((outcome, index) => (
                <Paper key={index} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Name"
                        value={outcome.name}
                        onChange={(e) => handleNestedArrayChange('secondary_outcomes', index, 'name', e.target.value)}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Measure"
                        value={outcome.measure}
                        onChange={(e) => handleNestedArrayChange('secondary_outcomes', index, 'measure', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Timeframe"
                        value={outcome.timeframe}
                        onChange={(e) => handleNestedArrayChange('secondary_outcomes', index, 'timeframe', e.target.value)}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={outcome.safety_issue}
                            onChange={(e) => handleNestedArrayChange('secondary_outcomes', index, 'safety_issue', e.target.checked)}
                          />
                        }
                        label="Safety Issue"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <IconButton
                        onClick={() => handleRemoveArrayItem('secondary_outcomes', index)}
                        disabled={formData.secondary_outcomes.length === 1}
                      >
                        <DeleteIcon />
                      </IconButton>
                      {index === formData.secondary_outcomes.length - 1 && (
                        <IconButton
                          onClick={() => handleAddArrayItem('secondary_outcomes', { name: '', measure: '', timeframe: '', safety_issue: false })}
                        >
                          <AddIcon />
                        </IconButton>
                      )}
                    </Grid>
                  </Grid>
                </Paper>
              ))}
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Other Outcomes
              </Typography>
              {formData.other_outcomes.map((outcome, index) => (
                <Paper key={index} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Name"
                        value={outcome.name}
                        onChange={(e) => handleNestedArrayChange('other_outcomes', index, 'name', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Measure"
                        value={outcome.measure}
                        onChange={(e) => handleNestedArrayChange('other_outcomes', index, 'measure', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Timeframe"
                        value={outcome.timeframe}
                        onChange={(e) => handleNestedArrayChange('other_outcomes', index, 'timeframe', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={outcome.safety_issue}
                            onChange={(e) => handleNestedArrayChange('other_outcomes', index, 'safety_issue', e.target.checked)}
                          />
                        }
                        label="Safety Issue"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <IconButton
                        onClick={() => handleRemoveArrayItem('other_outcomes', index)}
                        disabled={formData.other_outcomes.length === 1}
                      >
                        <DeleteIcon />
                      </IconButton>
                      {index === formData.other_outcomes.length - 1 && (
                        <IconButton
                          onClick={() => handleAddArrayItem('other_outcomes', { name: '', measure: '', timeframe: '', safety_issue: false })}
                        >
                          <AddIcon />
                        </IconButton>
                      )}
                    </Grid>
                  </Grid>
                </Paper>
              ))}
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Interventions */}
      <Accordion expanded={expandedSections.interventions} onChange={() => handleSectionToggle('interventions')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight="medium">
            8. Interventions & Arms
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Interventions
              </Typography>
              {formData.interventions.map((intervention, index) => (
                <Paper key={index} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Name"
                        value={intervention.name}
                        onChange={(e) => handleNestedArrayChange('interventions', index, 'name', e.target.value)}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Type"
                        value={intervention.type}
                        onChange={(e) => handleNestedArrayChange('interventions', index, 'type', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Description"
                        value={intervention.description}
                        onChange={(e) => handleNestedArrayChange('interventions', index, 'description', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <IconButton
                        onClick={() => handleRemoveArrayItem('interventions', index)}
                        disabled={formData.interventions.length === 1}
                      >
                        <DeleteIcon />
                      </IconButton>
                      {index === formData.interventions.length - 1 && (
                        <IconButton
                          onClick={() => handleAddArrayItem('interventions', { name: '', type: '', description: '' })}
                        >
                          <AddIcon />
                        </IconButton>
                      )}
                    </Grid>
                  </Grid>
                </Paper>
              ))}
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Arms
              </Typography>
              {formData.arms.map((arm, index) => (
                <Paper key={index} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Arm Title"
                        value={arm.arm_title}
                        onChange={(e) => handleNestedArrayChange('arms', index, 'arm_title', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Arm Type"
                        value={arm.arm_type}
                        onChange={(e) => handleNestedArrayChange('arms', index, 'arm_type', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Description"
                        value={arm.description}
                        onChange={(e) => handleNestedArrayChange('arms', index, 'description', e.target.value)}
                        multiline
                        rows={2}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" sx={{ mb: 1, display: 'block' }}>
                        Interventions (comma-separated)
                      </Typography>
                      <TextField
                        fullWidth
                        value={arm.interventions.join(', ')}
                        onChange={(e) => {
                          const interventions = e.target.value.split(',').map((s) => s.trim()).filter(Boolean);
                          handleNestedArrayChange('arms', index, 'interventions', interventions);
                        }}
                        placeholder="Enter intervention names separated by commas"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <IconButton
                        onClick={() => handleRemoveArrayItem('arms', index)}
                        disabled={formData.arms.length === 1}
                      >
                        <DeleteIcon />
                      </IconButton>
                      {index === formData.arms.length - 1 && (
                        <IconButton
                          onClick={() => handleAddArrayItem('arms', { arm_title: '', arm_type: '', description: '', interventions: [''] })}
                        >
                          <AddIcon />
                        </IconButton>
                      )}
                    </Grid>
                  </Grid>
                </Paper>
              ))}
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Ethics & Regulatory */}
      <Accordion expanded={expandedSections.ethics} onChange={() => handleSectionToggle('ethics')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight="medium">
            9. Ethics & Regulatory
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Ethics Approval Status"
                value={formData.ethics_approval_status}
                onChange={(e) => handleChange('ethics_approval_status', e.target.value)}
                required
              >
                {ethicsApprovalStatusOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Ethics Approval Date"
                value={formData.ethics_approval_date}
                onChange={(e) => handleChange('ethics_approval_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Ethics Committees
              </Typography>
              {formData.ethics_committees.map((item, index) => (
                <Grid container spacing={2} key={index} sx={{ mb: 1 }}>
                  <Grid item xs={10}>
                    <TextField
                      fullWidth
                      label={`Ethics Committee ${index + 1}`}
                      value={item}
                      onChange={(e) => handleArrayChange('ethics_committees', index, e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <IconButton
                      onClick={() => handleRemoveArrayItem('ethics_committees', index)}
                      disabled={formData.ethics_committees.length === 1}
                    >
                      <DeleteIcon />
                    </IconButton>
                    {index === formData.ethics_committees.length - 1 && (
                      <IconButton onClick={() => handleAddArrayItem('ethics_committees', '')}>
                        <AddIcon />
                      </IconButton>
                    )}
                  </Grid>
                </Grid>
              ))}
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.is_fda_regulated_drug}
                    onChange={(e) => handleChange('is_fda_regulated_drug', e.target.checked)}
                  />
                }
                label="FDA Regulated Drug"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.is_fda_regulated_device}
                    onChange={(e) => handleChange('is_fda_regulated_device', e.target.checked)}
                  />
                }
                label="FDA Regulated Device"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="FDA IND/IDE Number"
                value={formData.fda_ind_number}
                onChange={(e) => handleChange('fda_ind_number', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="DCGI Approval Status"
                value={formData.dcgi_approval_status}
                onChange={(e) => handleChange('dcgi_approval_status', e.target.value)}
                placeholder="e.g., Applied, Approved, Not applicable"
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* IPD Sharing */}
      <Accordion expanded={expandedSections.ipd} onChange={() => handleSectionToggle('ipd')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight="medium">
            10. IPD Sharing Plan
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.ipd_sharing}
                    onChange={(e) => handleChange('ipd_sharing', e.target.checked)}
                  />
                }
                label="Will Individual Participant Data (IPD) be shared?"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="IPD Plan Description"
                value={formData.ipd_plan_description}
                onChange={(e) => handleChange('ipd_plan_description', e.target.value)}
                multiline
                rows={3}
              />
            </Grid>
            {formData.ipd_sharing && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    IPD Plan Details
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="What Data"
                    value={formData.ipd_plan_details.what_data}
                    onChange={(e) => handleChange('ipd_plan_details.what_data', e.target.value)}
                    multiline
                    rows={2}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="When Available"
                    value={formData.ipd_plan_details.when_available}
                    onChange={(e) => handleChange('ipd_plan_details.when_available', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Who Can Access"
                    value={formData.ipd_plan_details.who_can_access}
                    onChange={(e) => handleChange('ipd_plan_details.who_can_access', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Access Criteria"
                    value={formData.ipd_plan_details.access_criteria}
                    onChange={(e) => handleChange('ipd_plan_details.access_criteria', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Data Location"
                    value={formData.ipd_plan_details.data_location}
                    onChange={(e) => handleChange('ipd_plan_details.data_location', e.target.value)}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Documents */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept=".pdf,.doc,.docx,.xml"
        onChange={handleDocumentFileSelect}
      />
      <Accordion expanded={expandedSections.documents} onChange={() => handleSectionToggle('documents')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight="medium">
            11. Related Documents
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            {/* Protocol Document */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Protocol Document</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start" sx={{ mb: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={loadingProtocolPdf ? <CircularProgress size={16} /> : <PictureAsPdfIcon />}
                  onClick={handleOpenCurrentProtocolPdf}
                  disabled={!projectId || loadingProtocolPdf}
                >
                  {loadingProtocolPdf ? 'Loading…' : 'Current protocol PDF'}
                </Button>
                <Button
                  component={Link}
                  to={projectId ? `/document-editor/protocol/${projectId}` : '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  size="small"
                  startIcon={<OpenInNewIcon />}
                  disabled={!projectId}
                >
                  Open Protocol Editor
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={uploading && uploadTarget === 'protocol_document' ? <CircularProgress size={16} /> : <UploadIcon />}
                  onClick={() => handleDocumentUploadClick('protocol_document')}
                  disabled={uploading}
                >
                  Upload
                </Button>
              </Stack>
              <TextField
                fullWidth
                label="Protocol document link or identifier"
                value={formData.protocol_document}
                onChange={(e) => handleChange('protocol_document', e.target.value)}
                placeholder="Paste link after upload or export PDF from Protocol Editor"
              />
            </Grid>
            {/* SAP Document */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>SAP Document</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start" sx={{ mb: 1 }}>
                <Button
                  component={Link}
                  to={projectId ? `/document-editor/sap/${projectId}` : '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  size="small"
                  startIcon={<OpenInNewIcon />}
                  disabled={!projectId}
                >
                  Open SAP Editor
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={uploading && uploadTarget === 'sap_document' ? <CircularProgress size={16} /> : <UploadIcon />}
                  onClick={() => handleDocumentUploadClick('sap_document')}
                  disabled={uploading}
                >
                  Upload
                </Button>
              </Stack>
              <TextField
                fullWidth
                label="SAP document link or identifier"
                value={formData.sap_document}
                onChange={(e) => handleChange('sap_document', e.target.value)}
                placeholder="Link or ID for the Statistical Analysis Plan document"
              />
            </Grid>
            {/* CSR Document */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>CSR Document</Typography>
              <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={uploading && uploadTarget === 'csr_document' ? <CircularProgress size={16} /> : <UploadIcon />}
                  onClick={() => handleDocumentUploadClick('csr_document')}
                  disabled={uploading}
                >
                  Upload
                </Button>
              </Stack>
              <TextField
                fullWidth
                label="CSR document link or identifier"
                value={formData.csr_document}
                onChange={(e) => handleChange('csr_document', e.target.value)}
                placeholder="Link or ID for the Clinical Study Report or summary results document"
              />
            </Grid>
            {/* Additional Documents */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Additional Documents</Typography>
              <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={uploading && uploadTarget === 'additional_documents' ? <CircularProgress size={16} /> : <UploadIcon />}
                  onClick={() => handleDocumentUploadClick('additional_documents')}
                  disabled={uploading}
                >
                  Upload
                </Button>
              </Stack>
              <TextField
                fullWidth
                label="Additional documents (link or identifier; comma-separated for multiple)"
                value={formData.additional_documents}
                onChange={(e) => handleChange('additional_documents', e.target.value)}
                placeholder="Paste link(s) after upload"
                multiline
                minRows={1}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Registry Selector Dialog */}
      <RegistrySelectorDialog
        open={registrySelectorOpen}
        onClose={handleCloseRegistrySelector}
        onConfirm={handleAutoPopulateRegistries}
        loading={autoPopulating}
      />
    </Box>
  );
};

export default CanonicalDataset;

